import { montarPaginacao, paginacaoDa, termoDa, type PaginacaoNaUrl } from '@/lib/paginacao-na-url';

/* Reexportados para a pagina ter UM import do assunto, e para que trocar a
   paginacao de lugar nao obrigue a mexer na tela. */
export { OPCOES_POR_PAGINA, PADRAO_POR_PAGINA } from '@/lib/paginacao-na-url';

import type { EstadoDoVencimento } from './api';

/**
 * O estado dos filtros da manutenção vive na URL (p.16).
 *
 * O motivo é de uso, não de arquitetura: "olha o que venceu nessa máquina"
 * precisa ser um link que se manda, e não uma sequência de cliques que o outro
 * tem que repetir. Antes os dois filtros moravam em `useState`: recarregar
 * voltava à lista inteira, e mandar o link mandava uma tela DIFERENTE da que
 * se estava vendo — o que é pior do que não poder mandar, porque quem recebe
 * acha que está vendo a mesma coisa.
 *
 * A ORDEM DOS ESTADOS mora aqui, e não na página, porque o validador e a
 * barra de filtros precisam do mesmo vocabulário: duas listas de estado
 * divergiriam, e a do validador passaria a recusar o que a barra oferece.
 */
export const ORDEM_DOS_FILTROS: readonly EstadoDoVencimento[] = [
  'vencida',
  'vence_logo',
  'sem_ponto_de_partida',
  'sem_leitura',
  'em_dia',
];

export type FiltroDeEstado = EstadoDoVencimento | '';

export function ehEstadoDoVencimento(valor: unknown): valor is EstadoDoVencimento {
  return typeof valor === 'string' && (ORDEM_DOS_FILTROS as readonly string[]).includes(valor);
}

export interface BuscaDaManutencao extends PaginacaoNaUrl {
  readonly estado?: EstadoDoVencimento;
  readonly maquina?: string;
}

/**
 * A URL é entrada do usuário: qualquer coisa chega ali, inclusive colada à
 * mão. Valor fora do vocabulário é DESCARTADO, nunca corrigido no chute.
 *
 * `maquina` é o ÚNICO que passa como texto livre, e é de propósito: a lista de
 * máquinas vem dos dados, não de um vocabulário fechado, e recusar um código
 * que a empresa não tem exigiria carregar a frota só para validar a URL. Um
 * código inexistente cai no estado vazio filtrado, que já diz o que fazer.
 */
export function validarBuscaDaManutencao(entrada: Record<string, unknown>): BuscaDaManutencao {
  const estado = entrada['estado'];
  const maquina = termoDa(entrada['maquina']);
  return {
    ...(ehEstadoDoVencimento(estado) ? { estado } : {}),
    ...(maquina === undefined ? {} : { maquina }),
    ...paginacaoDa(entrada),
  };
}

/** Monta a busca para navegar, deixando de fora tudo que já é o padrão. */
export function montarBuscaDaManutencao(estado: {
  readonly estado: FiltroDeEstado;
  readonly maquina: string;
  readonly pagina: number;
  readonly porPagina: number;
}): BuscaDaManutencao {
  const maquina = termoDa(estado.maquina);
  return {
    ...(estado.estado === '' ? {} : { estado: estado.estado }),
    ...(maquina === undefined ? {} : { maquina }),
    ...montarPaginacao(estado),
  };
}
